/**
 * `consumeTextStream` — the SSE text-stream consumer shared by core and the
 * mini-apps (Action View's `consumeAiSummaryStream` is a thin call to it).
 * Lives in `src/shared` so both `web/app-home` and `mini-apps/_shared` can
 * re-export it; it uses relative imports because the root tsconfig has no
 * `@shared/*` alias.
 */

import { SseTextFrameSchema } from './schemas/sse-text-stream';
import { safeParseJson } from './parse-json';
import { z } from 'zod';

/** Shown when the server sent an error frame with no usable message. */
const DEFAULT_ERROR_MESSAGE = 'AI generation failed';

export interface ConsumeTextStreamHandlers {
  /** Called with the running accumulation on each `delta` SSE event. */
  onDelta: (accumulated: string) => void;
  /** Called with the final accumulation on a `done` SSE event. */
  onDone: (accumulated: string) => void;
  /** Called with the error message on a failed response or `error` event. */
  onError: (message: string) => void;
}

/**
 * Consume an SSE `Response` whose frames are `{type:'delta',text}` /
 * `{type:'done'}` / `{type:'error',message}` — identical wire contract to
 * `src/api/flow/flow-contacts-intel.ts` and Action View's `/ai-summary`.
 *
 * - `!res.ok`: reads `res.text()` (falling back to a status message) and
 *   calls `onError`, returning `null` (nothing streamed).
 * - A missing body stream: calls `onError('No response stream')`, returns `null`.
 * - On success: reads `res.body` as a stream, parsing `data: ` SSE lines and
 *   dispatching `delta` → `onDelta`, `done` → `onDone`, `error` → `onError`.
 *   Returns the final accumulated text.
 *
 * Every decoded frame is validated through `SseTextFrameSchema` (D11) before
 * any callback runs — the wire is a trust boundary, and a `delta` whose
 * `text` arrived as an object must never land in the summary as
 * `[object Object]`.
 */
export async function consumeTextStream(
  res: Response,
  handlers: ConsumeTextStreamHandlers,
): Promise<string | null> {
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    handlers.onError(text || `Request failed: ${res.status}`);
    return null;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    handlers.onError('No response stream');
    return null;
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let accumulated = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    // Keep incomplete last line in buffer.
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const json = line.slice(6).trim();
      if (!json) continue;

      const decoded = safeParseJson(z.unknown(), json);
      // Skip malformed JSON lines.
      if (!decoded.success) continue;
      const raw = decoded.data;

      const frame = SseTextFrameSchema.safeParse(raw);
      if (!frame.success) {
        // A frame that fails validation is skipped exactly like malformed
        // JSON — except a malformed *error* frame, which would otherwise
        // leave the UI spinning forever with no failure ever surfaced.
        if (typeof raw === 'object' && raw !== null && (raw as { type?: unknown }).type === 'error') {
          handlers.onError(DEFAULT_ERROR_MESSAGE);
        }
        continue;
      }

      if (frame.data.type === 'delta') {
        // An empty-string delta is valid on the wire but has nothing to
        // append — don't fire a no-op re-render (pre-existing behavior).
        if (!frame.data.text) continue;
        accumulated += frame.data.text;
        handlers.onDelta(accumulated);
      } else if (frame.data.type === 'done') {
        handlers.onDone(accumulated);
      } else {
        handlers.onError(frame.data.message || DEFAULT_ERROR_MESSAGE);
      }
    }
  }

  return accumulated;
}
